// src/Redux/features/home/homeSlice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getMetaData,
  creatorOnboardingStatus,
  getFreelancerOnboardingDetails,
  getMyPackages,
  getMyJoinRequests
} from '../../../axios/ServerRequest';
import LocalStorage from '../../../utils/LocalStorage';
import { PackageDetails, PackageJoinRequest } from '../../../types/api.types';

interface OnboardingData {
  profileCompletion?: number;
  submitted?: boolean;
  personalDetails?: {
    profilePic?: string;
  };
  professionalDetails?: {
    skills?: any[];
    additionalDetails?: {
      certificationDetails?: any;
      projects?: any;
    };
  };
}

interface CreatorOnboardingData {
  userId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'DRAFT' | 'ON_HOLD';
  details: any;
  modifiedDate: string;
}

interface HomeState {
  metadata: any;
  onboardingData: OnboardingData | null;
  creatorOnboardingData: CreatorOnboardingData[] | null;
  packages: PackageDetails[];
  joinRequests: PackageJoinRequest[];
  loading: boolean;
  error: string | null;
}

const initialState: HomeState = {
  metadata: null,
  onboardingData: null,
  creatorOnboardingData: null,
  packages: [],
  joinRequests: [],
  loading: false,
  error: null,
};

// Existing metadata fetch
export const fetchMetadata = createAsyncThunk('home/fetchMetadata', async (_, { getState }) => {
  console.log('🟢 API CALL: fetchMetadata - START');
  try {
    const state = getState() as any;
    // Skip if metadata is already loaded
    if (state.home.metadata) {
      console.log('⏭️ API CALL: fetchMetadata - SKIPPED (already loaded)');
      return state.home.metadata;
    }

    console.log('🌐 API CALL: fetchMetadata - MAKING REQUEST');
    const response = await getMetaData();
    if (response.status === 200) {
      LocalStorage.setMetaData(response.data);
      console.log('✅ API CALL: fetchMetadata - SUCCESS');
      return response.data;
    }
    throw new Error('Failed to fetch metadata');
  } catch (error) {
    console.error('❌ API CALL: fetchMetadata - ERROR:', error);
    throw error;
  }
});

// Creator onboarding status
export const fetchCreatorOnboardingStatus = createAsyncThunk(
  'home/fetchCreatorOnboardingStatus',
  async (userId: string, { getState }) => {
    console.log('🟢 API CALL: fetchCreatorOnboardingStatus - START', { userId });
    try {
      const state = getState() as any;
      // Skip if creator onboarding data is already loaded
      if (state.home.creatorOnboardingData) {
        console.log('⏭️ API CALL: fetchCreatorOnboardingStatus - SKIPPED (already loaded)');
        return state.home.creatorOnboardingData;
      }

      console.log('🌐 API CALL: fetchCreatorOnboardingStatus - MAKING REQUEST');
      const response = await creatorOnboardingStatus(userId);
      if (response.status === 200) {
        LocalStorage.setCreatorOnboarding(response.data);
        console.log('✅ API CALL: fetchCreatorOnboardingStatus - SUCCESS');
        return response.data;
      }
      throw new Error('Failed to fetch creator onboarding status');
    } catch (error) {
      console.error('❌ API CALL: fetchCreatorOnboardingStatus - ERROR:', error);
      // throw error;
    }
  }
);

// Freelancer onboarding details
export const fetchOnboardingDetails = createAsyncThunk(
  'home/fetchOnboardingDetails',
  async (userId: string, { rejectWithValue, getState }) => {
    console.log('🟢 API CALL: fetchOnboardingDetails - START', { userId });
    try {
      const state = getState() as any;
      // Skip if onboarding data is already loaded
      if (state.home.onboardingData) {
        console.log('⏭️ API CALL: fetchOnboardingDetails - SKIPPED (already loaded)');
        return state.home.onboardingData;
      }

      console.log('🌐 API CALL: fetchOnboardingDetails - MAKING REQUEST');
      const response = await getFreelancerOnboardingDetails(userId);
      if (response.status === 200 && response.data) {
        LocalStorage.setOnboardingDetails(response.data);
        console.log('✅ API CALL: fetchOnboardingDetails - SUCCESS');
        return response.data;
      }
      throw new Error('No onboarding data found');
    } catch (error: any) {
      console.error('❌ API CALL: fetchOnboardingDetails - ERROR:', error);
      console.error('❌ API CALL: fetchOnboardingDetails - ERROR STATUS:', error?.response?.status);

      // If it's a 404, it means the user hasn't completed onboarding
      // This is expected for new users, so we should navigate to tutorials
      if (error?.response?.status === 404) {
        console.log('ℹ️ API CALL: fetchOnboardingDetails - 404 (new user), should navigate to tutorials');
        // Return a special value to indicate 404 instead of throwing error
        return rejectWithValue({
          status: 404,
          message: 'No onboarding data found - new user',
          shouldNavigateToTutorials: true
        });
      }

      // For other errors, throw as usual
      throw error;
    }
  }
);

// Fetch creator packages
export const fetchCreatorPackages = createAsyncThunk(
  'home/fetchCreatorPackages',
  async (_, { getState }) => {
    console.log('🟢 API CALL: fetchCreatorPackages - START');
    try {
      const state = getState() as any;
      // Skip if packages are already loaded
      if (state.home.packages && state.home.packages.length > 0) {
        console.log('⏭️ API CALL: fetchCreatorPackages - SKIPPED (already loaded)');
        return state.home.packages;
      }

      console.log('🌐 API CALL: fetchCreatorPackages - MAKING REQUEST');
      const response = await getMyPackages();
      console.log('✅ API CALL: fetchCreatorPackages - SUCCESS', { packageCount: response.data?.length || 0 });
      return response.data || [];
    } catch (error) {
      console.error('❌ API CALL: fetchCreatorPackages - ERROR:', error);
      return [];
    }
  }
);

// Fetch my join requests (for freelancers)
export const fetchMyJoinRequests = createAsyncThunk(
  'home/fetchMyJoinRequests',
  async (_, { getState }) => {
    console.log('🟢 API CALL: fetchMyJoinRequests - START');
    try {
      const state = getState() as any;
      // Skip if join requests are already loaded
      if (state.home.joinRequests && state.home.joinRequests.length > 0) {
        console.log('⏭️ API CALL: fetchMyJoinRequests - SKIPPED (already loaded)');
        return state.home.joinRequests;
      }

      console.log('🌐 API CALL: fetchMyJoinRequests - MAKING REQUEST');
      const response = await getMyJoinRequests();
      console.log('✅ API CALL: fetchMyJoinRequests - SUCCESS', { joinRequestCount: response.data?.length || 0 });
      return response.data || [];
    } catch (error) {
      console.error('❌ API CALL: fetchMyJoinRequests - ERROR:', error);
      return [];
    }
  }
);

const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    // Add any synchronous reducers here if needed
  },
  extraReducers: (builder) => {
    // Metadata
    builder
      .addCase(fetchMetadata.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMetadata.fulfilled, (state, action) => {
        state.loading = false;
        state.metadata = action.payload;
      })
      .addCase(fetchMetadata.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch metadata';
      });

    // Creator Onboarding
    builder
      .addCase(fetchCreatorOnboardingStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreatorOnboardingStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.creatorOnboardingData = action.payload;
      })
      .addCase(fetchCreatorOnboardingStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch creator onboarding status';
      });

    // Onboarding Details
    builder
      .addCase(fetchOnboardingDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOnboardingDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.onboardingData = action.payload;
      })
      .addCase(fetchOnboardingDetails.rejected, (state, action) => {
        state.loading = false;

        // Check if this is a 404 case (new user without onboarding data)
        const payload = action.payload as any;
        if (payload?.status === 404 && payload?.shouldNavigateToTutorials) {
          // Don't set this as an error - it's expected for new users
          state.error = null;
          state.onboardingData = null;
          console.log('New user detected - no onboarding data (404)');
        } else {
          // For other errors, set the error message
          state.error = action.error.message || 'Failed to fetch onboarding details';
        }
      });

    // Creator Packages
    builder
      .addCase(fetchCreatorPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreatorPackages.fulfilled, (state, action) => {
        state.loading = false;
        state.packages = action.payload;
      })
      .addCase(fetchCreatorPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch creator packages';
        state.packages = [];
      });

    // My Join Requests
    builder
      .addCase(fetchMyJoinRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyJoinRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.joinRequests = action.payload;
      })
      .addCase(fetchMyJoinRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch join requests';
        state.joinRequests = [];
      });
  },
});

export default homeSlice.reducer;